import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ConfirmHost } from "./components/ConfirmDialog";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { useApi } from "./lib/useApi";
import { getWorkspaceConfig } from "./lib/api";
import { LoginPage } from "./pages/LoginPage";
import { TicketsPage } from "./pages/TicketsPage";
import { MyWorkPage } from "./pages/MyWorkPage";
import { ActionItemsPage } from "./pages/ActionItemsPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { AccessRequestsPage } from "./pages/AccessRequestsPage";
import { GoalsPage } from "./pages/GoalsPage";
import { DashboardsPage } from "./pages/DashboardsPage";
import { DashboardDetailPage } from "./pages/DashboardDetailPage";
import { AssetsPage } from "./pages/AssetsPage";
import { AssetDetailPage } from "./pages/AssetDetailPage";
import { AssignmentsPage } from "./pages/AssignmentsPage";
import { FacilitiesPage } from "./pages/FacilitiesPage";
import { FacilityAssetsPage } from "./pages/FacilityAssetsPage";
import { FacilityAssetDetailPage } from "./pages/FacilityAssetDetailPage";
import { FacilityPartsPage } from "./pages/FacilityPartsPage";
import { FacilityChatPage } from "./pages/FacilityChatPage";
import { ChangeManagementPage } from "./pages/ChangeManagementPage";
import { ChangeRequestDetailPage } from "./pages/ChangeRequestDetailPage";
import { ReportsPage } from "./pages/ReportsPage";
import { FormsPage } from "./pages/FormsPage";
import { KnowledgeBasePage } from "./pages/KnowledgeBasePage";
import { AutomationsPage } from "./pages/AutomationsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SsoCompletePage } from "./pages/SsoCompletePage";
import { PublicFormPage } from "./pages/PublicFormPage";
import { PublicFlowPage } from "./pages/PublicFlowPage";
import { CsatPage } from "./pages/CsatPage";

/** Sends an authenticated member to the tenant's configured default landing page
 *  (Settings → General), falling back to Tickets while it loads or when unset. */
function LandingRedirect() {
  const { data, loading } = useApi((signal) => getWorkspaceConfig(signal), []);
  if (loading) return null;
  return <Navigate to={data?.config.landing || "/tickets"} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <ConfirmHost />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso-complete" element={<SsoCompletePage />} />
        {/* Public, no-login intake form (portal-enabled forms only). */}
        <Route path="/f/:slug" element={<PublicFormPage />} />
        {/* Public, no-login ticket-flow intake (public-enabled flows only). */}
        <Route path="/flow/:slug" element={<PublicFlowPage />} />
        {/* Public, no-login CSAT rating page (tokenized, one ticket). */}
        <Route path="/csat/:token" element={<CsatPage />} />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          {/* Both routes render the inbox shell; `:id` selects the reading pane. */}
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:id" element={<TicketsPage />} />
          <Route path="/my-work" element={<MyWorkPage />} />
          <Route path="/action-items" element={<ActionItemsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/access-requests" element={<AccessRequestsPage />} />
          <Route path="/goals" element={<GoalsPage />} />
          <Route path="/dashboards" element={<DashboardsPage />} />
          <Route path="/dashboards/:id" element={<DashboardDetailPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/assets/assignments" element={<AssignmentsPage />} />
          <Route path="/assets/:id" element={<AssetDetailPage />} />
          {/* Facilities (CMMS). Both /facilities and /facilities/:id render the
              work-order queue shell; the equipment routes are declared before the
              :id route so "assets" isn't captured as a work-order id. */}
          <Route path="/facilities" element={<FacilitiesPage />} />
          <Route path="/facilities/assets" element={<FacilityAssetsPage />} />
          <Route path="/facilities/assets/:id" element={<FacilityAssetDetailPage />} />
          <Route path="/facilities/parts" element={<FacilityPartsPage />} />
          <Route path="/facilities/chat" element={<FacilityChatPage />} />
          <Route path="/facilities/:id" element={<FacilitiesPage />} />
          {/* Change Management (work type "change_management"). */}
          <Route path="/change-management" element={<ChangeManagementPage />} />
          <Route path="/change-management/:id" element={<ChangeRequestDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/kb" element={<KnowledgeBasePage />} />
          <Route path="/automations" element={<AutomationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route
          path="/"
          element={
            <RequireAuth>
              <LandingRedirect />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/tickets" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
